import { computed, ref, watch } from "vue";
import type { ComputedRef } from "vue";

export type ThemeMode = "light" | "dark" | "auto";

const STORAGE_KEY = "accountv2-theme-preference";
const DARK_MODE_START = 18; // 6 PM
const DARK_MODE_END = 6; // 6 AM
const DARK_CLASS = "dark";
const MIN_TRANSITION_DELAY_MS = 1_000; // Safety buffer so timers aren't rescheduled instantly if calculations hit the exact transition moment

type ResolvedTheme = "light" | "dark";

export interface ThemeContext {
  isDark: ComputedRef<boolean>;
  preference: ComputedRef<ThemeMode>;
  activeMode: ComputedRef<ResolvedTheme>;
  setTheme: (mode: ThemeMode) => void;
}

/**
 * Detects system color scheme preference
 * Returns true if dark mode is preferred by the system
 */
function getSystemPreference(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Determines if it's currently night time based on local time
 * Dark mode: 6 PM - 6 AM (18:00 - 06:00)
 */
function isNightTime(): boolean {
  const hour = new Date().getHours();
  return hour >= DARK_MODE_START || hour < DARK_MODE_END;
}

/**
 * Resolves the effective theme mode based on preference and system/time
 */
function resolveTheme(preference: ThemeMode): boolean {
  if (preference === "dark") return true;
  if (preference === "light") return false;

  // Auto mode: use time-based intelligence, falling back to system preference
  if (isNightTime()) {
    return true; // Night time → dark mode
  }
  return getSystemPreference(); // Day time → use system preference
}

/**
 * Applies theme class to document root
 */
function applyTheme(isDark: boolean): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.dataset.theme = isDark ? "dark" : "light";
  if (isDark) {
    root.classList.add(DARK_CLASS);
  } else {
    root.classList.remove(DARK_CLASS);
  }
}

function getNextTransition(from: Date): Date {
  const nextMorning = new Date(from);
  nextMorning.setHours(DARK_MODE_END, 0, 0, 0);
  if (nextMorning <= from) {
    nextMorning.setDate(nextMorning.getDate() + 1);
  }

  const nextEvening = new Date(from);
  nextEvening.setHours(DARK_MODE_START, 0, 0, 0);
  if (nextEvening <= from) {
    nextEvening.setDate(nextEvening.getDate() + 1);
  }

  return nextMorning < nextEvening ? nextMorning : nextEvening;
}

/**
 * Hook for theme management with system preference and time-based intelligence
 */
export function useTheme() {
  const preferenceRef = ref<ThemeMode>("auto");
  let mediaQuery: MediaQueryList | null = null;
  let mediaQueryListener: ((event: MediaQueryListEvent) => void) | null = null;
  let timeUpdateTimer: number | null = null;
  let storageListener: ((event: StorageEvent) => void) | null = null;
  const resolvedThemeRef = ref<boolean>(resolveTheme(preferenceRef.value));

  // Load preference from localStorage on first use
  function loadPreference(): void {
    if (typeof localStorage === "undefined") return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "auto") {
      preferenceRef.value = stored;
    }
  }

  // Save preference to localStorage
  function savePreference(preference: ThemeMode): void {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, preference);
  }

  function clearScheduledUpdates(): void {
    if (timeUpdateTimer !== null && typeof window !== "undefined") {
      window.clearTimeout(timeUpdateTimer);
      timeUpdateTimer = null;
    }
  }

  function scheduleTimeUpdate(): void {
    if (typeof window === "undefined") return;
    if (preferenceRef.value !== "auto") {
      clearScheduledUpdates();
      return;
    }

    const now = new Date();
    const nextTransition = getNextTransition(now);
    const delay = Math.max(nextTransition.getTime() - now.getTime(), MIN_TRANSITION_DELAY_MS);

    clearScheduledUpdates();
    timeUpdateTimer = window.setTimeout(() => {
      updateTheme();
      scheduleTimeUpdate();
    }, delay);
  }

  // Update effective theme based on preference
  function updateTheme(): void {
    const isDark = resolveTheme(preferenceRef.value);
    resolvedThemeRef.value = isDark;
    applyTheme(isDark);
  }

  // Watch preference changes
  watch(
    preferenceRef,
    newPreference => {
      savePreference(newPreference);
      updateTheme();
      scheduleTimeUpdate();
    },
    { immediate: false }
  );

  // Computed getters
  const isDark = computed(() => resolvedThemeRef.value);
  const preference = computed(() => preferenceRef.value);
  const activeMode = computed<ResolvedTheme>(() => (resolvedThemeRef.value ? "dark" : "light"));

  // Set theme preference
  function setTheme(mode: ThemeMode): void {
    preferenceRef.value = mode;
  }

  // Initialize on first use
  function init(): void {
    loadPreference();
    updateTheme();
    scheduleTimeUpdate();

    // Listen for system preference changes
    if (typeof window !== "undefined") {
      mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      mediaQueryListener = () => {
        // Only update if in auto mode
        if (preferenceRef.value === "auto") {
          updateTheme();
          scheduleTimeUpdate();
        }
      };

      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", mediaQueryListener);
      } else if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(mediaQueryListener);
      }

      storageListener = event => {
        if (event.key !== STORAGE_KEY || typeof event.newValue !== "string") {
          return;
        }

        if (event.newValue === "light" || event.newValue === "dark" || event.newValue === "auto") {
          preferenceRef.value = event.newValue;
        }
      };

      window.addEventListener("storage", storageListener);
    }
  }

  function cleanup(): void {
    if (mediaQuery !== null && mediaQueryListener !== null) {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", mediaQueryListener);
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(mediaQueryListener);
      }
    }
    if (storageListener !== null && typeof window !== "undefined") {
      window.removeEventListener("storage", storageListener);
      storageListener = null;
    }
    clearScheduledUpdates();
  }

  return {
    isDark,
    preference,
    activeMode,
    setTheme,
    init,
    cleanup,
    // Exposed for testing/debugging
    isNightTime,
    getSystemPreference
  };
}
